import { Box, createStyles, Loader, LoadingOverlay, Tabs } from "@mantine/core";
import { RpiCard } from "../../rpi-core/card/RpiCard";
import { IconV3 } from "@rpi/openapi-core";
import { Button, tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { Body, Heading } from "../../components/Typography";
import React from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthUser } from "../../hooks/useAuthUser";
import { getRoutePath } from "../../hooks/useNavigateRoute";
import { OrderStatusCell } from "../../components/cells/OrderStatusCellV2";
import { CancelOrderDrawer } from "./CancelOrderDrawer";
import { OrderActionsMenu, useOverridePaymentStatusFlow } from "./OrderActionsMenu";
import { ReprintDrawer, isOrderReprintable } from "./ReprintDrawer";
import { RefundDrawer } from "./components/refund/RefundDrawer";

export type OrderViewV2OutletContext = {
  order: GraphQL.OrderQuery["orderByIdentifier"] | undefined;
};

const useStyles = createStyles(() => ({
  container: {
    height: "100%",
    overflowY: "auto" as const,
    padding: tokens.spacing[20],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[6],
  },
  backLink: {
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacing[2],
  },
  card: {
    display: "flex",
    flexDirection: "column" as const,
    padding: tokens.spacing[6],
    gap: tokens.spacing[6],
  },
  errorCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
    padding: tokens.spacing[6],
    gap: tokens.spacing[4],
    alignItems: "center",
    justifyContent: "center",
  },
  loadingContainer: {
    flex: 1,
    position: "relative" as const,
  },
  tabsContainer: {
    display: "flex",
    flexDirection: "column" as const,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[2],
  },
}));

const ORDER_TABS = [
  { label: "Details", path: "details" },
  { label: "Items", path: "items" },
  { label: "Event Logs", path: "event-logs" },
  { label: "Shipments", path: "shipments" },
] as const;

function findActiveTab(pathname: string, basePath: string): string {
  return ORDER_TABS.find((tab) => pathname === `${basePath}/${tab.path}`)?.path ?? "details";
}

export const OrderViewV2: React.FC = () => {
  const { customerPublicId, customerOrderId } = useParams();
  const user = useAuthUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [reprintOpened, setReprintOpened] = React.useState(false);
  const [refundOpened, setRefundOpened] = React.useState(false);
  const [cancelOpened, setCancelOpened] = React.useState(false);

  const { data, isLoading, error, refetch } = GraphQL.useOrderQuery(
    {
      orderIdentifier: {
        customerPublicId: customerPublicId ?? "",
        customerOrderId: customerOrderId ?? "",
      },
      isSandbox: user.isSandbox,
    },
    { enabled: !!customerPublicId && !!customerOrderId }
  );
  const order = data?.orderByIdentifier;

  // Hoist the override-payment-status modal above the early returns and
  // outside the actions Menu.Dropdown so it survives the menu's open/close
  // cycle. Picking a status closes the menu; if the modal lived inside the
  // dropdown subtree it would unmount before the user could confirm.
  const { openConfirm: openOverridePaymentStatus, modal: overridePaymentStatusModal } = useOverridePaymentStatusFlow(
    order ?? { customerOrderId: "", paymentStatus: null }
  );

  const { classes } = useStyles();

  const orderBasePath = getRoutePath("order", {
    customerPublicId: customerPublicId ?? "",
    customerOrderId: customerOrderId ?? "",
  });
  const activeTab = findActiveTab(location.pathname, orderBasePath);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <LoadingOverlay loader={<Loader color="brand" size="xl" />} visible={true} />
      </Box>
    );
  }

  if (error) {
    return (
      <div className={classes.container}>
        <Link to={getRoutePath("orders")} className={classes.backLink}>
          <IconV3 icon="chevron_left" size={20} />
          <Body weight="semibold">Back to All Orders</Body>
        </Link>
        <RpiCard className={classes.errorCard}>
          <Body>Failed to load order.</Body>
          <Button variant="outlined" intent="neutral" onClick={() => refetch()}>
            Retry
          </Button>
        </RpiCard>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <Link to={getRoutePath("orders")} className={classes.backLink}>
        <IconV3 icon="chevron_left" size={20} />
        <Body weight="semibold">Back to All Orders</Body>
      </Link>

      <RpiCard className={classes.card}>
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <Heading size="xl" weight="bold">
              {order?.customerOrderId}
            </Heading>
            <OrderStatusCell status={order?.publicOrderStatus} />
          </div>

          <OrderActionsMenu
            order={order}
            onReprintClick={() => setReprintOpened(true)}
            onRefundClick={() => setRefundOpened(true)}
            onCancelClick={() => setCancelOpened(true)}
            onOverridePaymentStatusPick={openOverridePaymentStatus}
          />
        </div>

        <Tabs
          value={activeTab}
          onTabChange={(value) => {
            if (value) navigate(value);
          }}
          className={classes.tabsContainer}
        >
          <Tabs.List>
            {ORDER_TABS.map((tab) => (
              <Tabs.Tab key={tab.path} value={tab.path}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          <Outlet context={{ order }} />
        </Tabs>
      </RpiCard>

      {isOrderReprintable(order) && (
        <ReprintDrawer opened={reprintOpened} onClose={() => setReprintOpened(false)} order={order} />
      )}
      <RefundDrawer opened={refundOpened} onClose={() => setRefundOpened(false)} order={order} />
      {order && <CancelOrderDrawer opened={cancelOpened} onClose={() => setCancelOpened(false)} order={order} />}
      {overridePaymentStatusModal}
    </div>
  );
};
